import React, { useEffect, useState } from 'react';
import {
  Button, Checkbox, Form, Input, Modal, Select, Typography, Upload,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useI18n } from '../i18n';
import { PRODUCT_DOCUMENT_TYPES } from '../constants/productTypes';

const { Option } = Select;
const { Text } = Typography;

export default function ProductDocumentModal({
  open,
  onCancel,
  onSubmit,
  editingDocument = null,
  product,
  currentUser,
}) {
  const { t } = useI18n();
  const [form] = Form.useForm();
  const isEditing = Boolean(editingDocument);

  const [fileList, setFileList] = useState([]);
  const [existingFile, setExistingFile] = useState(null);
  const [fileError, setFileError] = useState('');

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      name: editingDocument?.name || '',
      documentType: editingDocument?.documentType || 'Image',
      isPrimaryImage: editingDocument?.isPrimaryImage || false,
    });
    setFileList([]);
    setExistingFile(editingDocument
      ? { fileName: editingDocument.fileName, fileUrl: editingDocument.fileUrl }
      : null);
    setFileError('');
  }, [editingDocument, form, open]);

  const beforeUpload = (file) => {
    setFileError('');
    setFileList([file]);
    return false;
  };

  const handleRemoveFile = () => {
    setFileList([]);
  };

  const handleFinish = async () => {
    const values = await form.validateFields();

    if (fileList.length === 0 && !existingFile) {
      setFileError(t('A file is required'));
      return;
    }

    const newFile = fileList[0] || null;
    const documentData = {
      ...(editingDocument || {}),
      productId: product?.id,
      name: values.name.trim(),
      documentType: values.documentType,
      isPrimaryImage: values.documentType === 'Image' ? Boolean(values.isPrimaryImage) : false,
      inherited: editingDocument?.inherited || false,
      ...(newFile
        ? {
          fileName: newFile.name,
          fileUrl: URL.createObjectURL(newFile),
          uploadedBy: currentUser?.name || 'System',
          uploadDate: new Date().toISOString().slice(0, 16).replace('T', ' '),
        }
        : {}),
    };

    const success = onSubmit(documentData);
    if (success !== false) {
      setFileList([]);
      setFileError('');
    }
  };

  const currentFileLabel = fileList[0]?.name || existingFile?.fileName;
  const documentType = Form.useWatch('documentType', form);

  return (
    <Modal
      title={isEditing ? t('Edit Document') : t('Add Document')}
      open={open}
      onCancel={onCancel}
      width={520}
      footer={[
        <Button key="cancel" onClick={onCancel}>{t('Close')}</Button>,
        <Button key="submit" type="primary" onClick={handleFinish}>
          {isEditing ? t('Update') : t('Create')}
        </Button>,
      ]}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label={t('Document Type')}
          name="documentType"
          rules={[{ required: true, message: t('Please select document type') }]}
        >
          <Select>
            {PRODUCT_DOCUMENT_TYPES.map((type) => (
              <Option key={type} value={type}>{t(type)}</Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label={t('Name')}
          name="name"
          rules={[{ required: true, message: t('Name is required') }]}
        >
          <Input placeholder={t('Document name')} />
        </Form.Item>
        {documentType === 'Image' && (
          <Form.Item name="isPrimaryImage" valuePropName="checked">
            <Checkbox>{t('Set as Main Image')}</Checkbox>
          </Form.Item>
        )}
        <Form.Item label={t('File Upload')} required>
          <Upload
            beforeUpload={beforeUpload}
            onRemove={handleRemoveFile}
            fileList={fileList}
            accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.ai,.svg"
            maxCount={1}
          >
            <Button icon={<UploadOutlined />}>{t('Select File')}</Button>
          </Upload>
          {!fileList.length && existingFile && (
            <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
              {t('Current file')}: {currentFileLabel}
            </Text>
          )}
          {fileError && (
            <Text type="danger" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
              {fileError}
            </Text>
          )}
        </Form.Item>
      </Form>
    </Modal>
  );
}
